"use client"

import { isExternalUrl, setQueryParams } from "@primoui/utils"
import type { ComponentProps } from "react"
import { siteConfig } from "~/config/site"
import { useTrackEvent } from "~/hooks/use-track-event"
import { type ExternalLinkRelPolicy, getSafeExternalLinkRel } from "~/lib/link-rel"

type ExternalLinkProps = ComponentProps<"a"> & {
  doTrack?: boolean
  doFollow?: boolean
  relPolicy?: ExternalLinkRelPolicy
  eventName?: string
  /** Legacy alias for the existing Plausible dashboard when `eventName` is renamed. */
  plausibleEventName?: string
  eventProps?: Record<string, unknown>
  toolSlug?: string
}

export const ExternalLink = ({
  href,
  target = "_blank",
  doTrack = false,
  doFollow = false,
  rel,
  relPolicy,
  eventName,
  plausibleEventName,
  eventProps,
  toolSlug,
  ...props
}: ExternalLinkProps) => {
  const trackEvent = useTrackEvent()
  const addTracking = doTrack && !href?.includes("utm_source")
  const finalHref = addTracking ? setQueryParams(href!, { utm_source: siteConfig.domain }) : href
  const isExternal = isExternalUrl(finalHref)

  return (
    <a
      href={finalHref!}
      target={target}
      rel={getSafeExternalLinkRel({ doFollow, rel, relPolicy, target })}
      onClick={() => {
        if (!isExternal) return

        if (toolSlug) {
          const endpoint = `/api/tools/${encodeURIComponent(toolSlug)}/click`
          if (!navigator.sendBeacon?.(endpoint)) {
            void fetch(endpoint, { method: "POST", keepalive: true })
          }
        }

        if (eventName) {
          trackEvent(eventName, eventProps, plausibleEventName ? { plausibleEventName } : undefined)
        }
      }}
      {...props}
    />
  )
}
